import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Package, ShoppingCart, Calendar, ArrowUpRight, Clock, Target, PiggyBank, BarChart2, Sparkles, ArrowDownRight, ArrowLeft } from 'lucide-react';
import { useAppStore } from '../../shared/store';
const timeframeOptions = [{
  value: '1M',
  label: 'Deze Maand'
}, {
  value: '3M',
  label: 'Laatste 3 Maanden'
}, {
  value: '6M',
  label: 'Laatste 6 Maanden'
}, {
  value: '1Y',
  label: 'Dit Jaar'
}];
export function AnalyticsPage() {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState('3M');
  const {
    transactions,
    monthlyBudget
  } = useAppStore();
  const metrics = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const currentMonthSpending = transactions.filter(t => {
      const date = new Date(t.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    }).reduce((sum, t) => sum + t.amount, 0);
    const lastMonthSpending = transactions.filter(t => {
      const date = new Date(t.date);
      return date.getMonth() === currentMonth - 1 && date.getFullYear() === currentYear;
    }).reduce((sum, t) => sum + t.amount, 0);
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const daysPassed = now.getDate() || 1; // Prevent division by zero
    const dailyAverage = currentMonthSpending / daysPassed;
    const dailyBudget = monthlyBudget / daysInMonth;
    const budgetUtilization = currentMonthSpending / monthlyBudget * 100;
    const monthlyTrend = lastMonthSpending ? (currentMonthSpending - lastMonthSpending) / lastMonthSpending * 100 : 0;
    const totalSavings = transactions.reduce((sum, t) => sum + (t.savings || 0), 0);
    const totalSpent = transactions.reduce((sum, t) => sum + t.amount, 0);
    const savingsRate = totalSpent > 0 ? totalSavings / (totalSpent + totalSavings) * 100 : 0;
    const averageTransaction = transactions.length > 0 ? totalSpent / transactions.length : 0;
    return {
      totalSpent,
      monthlyTrend,
      currentMonthSpending,
      budgetUtilization,
      dailyAverage,
      dailyBudget,
      savingsRate,
      totalSavings,
      averageTransaction
    };
  }, [transactions, monthlyBudget]);
  const spendingTrends = useMemo(() => {
    const months = timeframe === '1M' ? 1 : timeframe === '3M' ? 3 : timeframe === '6M' ? 6 : 12;
    const today = new Date();
    const data = [];
    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthTransactions = transactions.filter(t => {
        const transDate = new Date(t.date);
        return transDate.getMonth() === date.getMonth() && transDate.getFullYear() === date.getFullYear();
      });
      const spending = monthTransactions.reduce((sum, t) => sum + t.amount, 0);
      const savings = monthTransactions.reduce((sum, t) => sum + (t.savings || 0), 0);
      data.push({
        month: date.toLocaleString('nl-NL', {
          month: 'short'
        }),
        spending,
        budget: monthlyBudget,
        savings
      });
    }
    return data;
  }, [monthlyBudget, timeframe, transactions]);
  const categoryData = useMemo(() => {
    const categoriesMap = new Map<string, number>();
    transactions.forEach(t => {
      const cat = t.category || 'Overig';
      categoriesMap.set(cat, (categoriesMap.get(cat) || 0) + t.amount);
    });
    const colors = ['hsl(var(--primary))', 'hsl(var(--success))', 'hsl(var(--warning))', 'hsl(var(--destructive))', '#8b5cf6', '#ec4899', '#06b6d4'];
    return Array.from(categoriesMap.entries()).map(([name, value], index) => ({
      name,
      value,
      color: colors[index % colors.length]
    })).sort((a, b) => b.value - a.value);
  }, [transactions]);
  return <div className="min-h-screen bg-background pb-24" data-id="element-1381" data-mp="10uy9p8w8sapr">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border pt-6 pb-4 px-6 space-y-4" data-id="element-1382" data-mp="10x2myhg16m2p">
        <div className="flex items-center gap-3" data-id="element-1383" data-mp="r556av6971wl">
          <button onClick={() => navigate('/profile')} className="p-2 -ml-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition-colors" data-id="element-1384" data-mp="r5og1olr2jzj">
            <ArrowLeft className="w-5 h-5" data-id="element-1385" data-mp="i3yngfg4tf06" />
          </button>
          <h1 className="text-2xl font-bold text-foreground" data-id="element-1386" data-mp="3unjb4bu58wpk">Mijn uitgaven</h1>
        </div>

        <div className="grid grid-cols-4 gap-1 rounded-xl border border-border bg-card p-1" role="tablist" aria-label="Periode kiezen" data-id="element-1387" data-mp="3pq19l6ighjrl">
          {timeframeOptions.map(option => <button key={option.value} type="button" onClick={() => setTimeframe(option.value)} className={`rounded-lg px-2 py-2 text-xs font-semibold transition-colors ${timeframe === option.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`} role="tab" aria-selected={timeframe === option.value} data-id="element-1388" data-mp="3ib6f3586qlj">
              {option.value}
            </button>)}
        </div>
      </div>

      <div className="space-y-6 px-6 pt-6" data-id="element-1389" data-mp="108tt037wntcv">
        <section className="rounded-2xl border border-primary/20 bg-primary/10 p-4" aria-labelledby="spending-intro-title" data-id="element-1390" data-mp="w6ozd4hgiwq8">
          <h2 id="spending-intro-title" className="font-semibold text-foreground" data-id="element-1391" data-mp="13aol0k8d9kbz">Zo staat je maand ervoor</h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground" data-id="element-1392" data-mp="18a1gtuh7u1qp">Zie wat je uitgeeft, of je binnen budget blijft en waar je voordeel pakt.</p>
        </section>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-4" data-id="element-1393" data-mp="1hzuq2otvy4rs">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-card border border-border rounded-2xl p-4" data-id="element-1394" data-mp="1i0m9z2e4ee28">
            <div className="flex items-center justify-between mb-3" data-id="element-1395" data-mp="20xx8ulvub5h5">
              <div className="p-2 rounded-xl bg-primary/10 text-primary" data-id="element-1396" data-mp="1kt7an7s8oc9">
                <Clock className="w-5 h-5" data-id="element-1397" data-mp="1mmi8epfhuq2" />
              </div>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${metrics.dailyAverage <= metrics.dailyBudget ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}`} data-id="element-1398" data-mp="6kfkwxriw6ll">
                {metrics.dailyAverage <= metrics.dailyBudget ? <ArrowDownRight className="w-3 h-3" data-id="element-1399" data-mp="2b241fzfnstiy" /> : <ArrowUpRight className="w-3 h-3" data-id="element-1400" data-mp="263kebvpbxbp0" />}
                {metrics.dailyAverage <= metrics.dailyBudget ? 'Onder' : 'Boven'}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-1" data-id="element-1401" data-mp="2yry8u1h7lpvt">
              Gemiddeld per dag
            </p>
            <p className="text-2xl font-bold text-foreground mb-1" data-id="element-1402" data-mp="2tu6ivd04kdu9">
              €{metrics.dailyAverage.toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground" data-id="element-1403" data-mp="1idg7zgkraxub">
              Richtbedrag: €{metrics.dailyBudget.toFixed(2)} per dag
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-card border border-border rounded-2xl p-4" data-id="element-1404" data-mp="1dfww2nbj6x5j">
            <div className="flex items-center justify-between mb-3" data-id="element-1405" data-mp="38ggvewf5oo0x">
              <div className="p-2 rounded-xl bg-success/10 text-success" data-id="element-1406" data-mp="38ith9ko1bij9">
                <BarChart2 className="w-5 h-5" data-id="element-1407" data-mp="1ypb08rzijy" />
              </div>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${metrics.monthlyTrend > 0 ? 'bg-destructive/10 text-destructive' : 'bg-success/10 text-success'}`} data-id="element-1408" data-mp="4z8eces95s79">
                {metrics.monthlyTrend > 0 ? <ArrowUpRight className="w-3 h-3" data-id="element-1409" data-mp="izj39ca04wps" /> : <TrendingDown className="w-3 h-3" data-id="element-1410" data-mp="nqre8cqzcq52" />}
                {Math.abs(metrics.monthlyTrend).toFixed(1)}%
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-1" data-id="element-1411" data-mp="28fbyv58q3k48">
              Deze maand
            </p>
            <p className="text-2xl font-bold text-foreground mb-1" data-id="element-1412" data-mp="23htodaf24wu4">
              €{metrics.currentMonthSpending.toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground" data-id="element-1413" data-mp="2d855jynr52iy">vergeleken met vorige maand</p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-card border border-border rounded-2xl p-4" data-id="element-1414" data-mp="1phspg0icrzha">
            <div className="flex items-center justify-between mb-3" data-id="element-1415" data-mp="1ug3wk3h9jvvt">
              <div className="p-2 rounded-xl bg-primary/10 text-primary" data-id="element-1416" data-mp="39x4cnaclny9k">
                <PiggyBank className="w-5 h-5" data-id="element-1417" data-mp="35wu5hud2n1t4" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-1" data-id="element-1418" data-mp="25lke2ekuvyjo">
              Voordeel op je aankopen
            </p>
            <p className="text-2xl font-bold text-foreground mb-1" data-id="element-1419" data-mp="21jgpqpd38zgz">
              {metrics.savingsRate.toFixed(1)}%
            </p>
            <p className="text-xs text-success" data-id="element-1420" data-mp="1wryywu4s4f9q">
              Gemiddeld €{metrics.averageTransaction.toFixed(2)} per boodschappenronde
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="bg-card border border-border rounded-2xl p-4" data-id="element-1421" data-mp="1mvcgsd3u82be">
            <div className="flex items-center justify-between mb-3" data-id="element-1422" data-mp="18x5pxgxb32l8">
              <div className="p-2 rounded-xl bg-primary/10 text-primary" data-id="element-1423" data-mp="387dvhwt2t54b">
                <Target className="w-5 h-5" data-id="element-1424" data-mp="339lw6hq3qgq3" />
              </div>
            </div>
            <p className="mb-1 text-xs text-muted-foreground" data-id="element-1425" data-mp="3asv384b4y3bz">Van je budget gebruikt</p>
            <p className="text-2xl font-bold text-foreground mb-2" data-id="element-1426" data-mp="3ar0stjxhptm5">
              {metrics.budgetUtilization.toFixed(0)}%
            </p>
            <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-1427" data-mp="3ki4yft1a024b">
              <div className={`h-full rounded-full transition-all duration-500 ${metrics.budgetUtilization > 90 ? 'bg-destructive' : metrics.budgetUtilization > 75 ? 'bg-warning' : 'bg-success'}`} style={{
              width: `${Math.min(100, metrics.budgetUtilization)}%`
            }} data-id="element-1428" data-mp="3phrjgoncimno" />
            </div>
          </motion.div>
        </div>

        {/* Spending Trend Chart */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-card border border-border rounded-2xl overflow-hidden" data-id="element-1429" data-mp="35utkjkhmg611">
          <div className="p-5 border-b border-border" data-id="element-1430" data-mp="2i2m88o8wu282">
            <div className="flex items-center gap-3" data-id="element-1431" data-mp="2d5lzu6ol8nds">
              <div className="p-2.5 rounded-xl bg-primary/10 text-primary" data-id="element-1432" data-mp="2d4szbng82u43">
                <TrendingUp className="w-5 h-5" data-id="element-1433" data-mp="2r3kfrgik4rve" />
              </div>
              <h2 className="text-lg font-semibold text-foreground" data-id="element-1434" data-mp="2m595x5s9ihd6">
                Uitgaven per maand
              </h2>
            </div>
          </div>
          <div className="p-5" data-id="element-1435" data-mp="1ye3paoehzl1t">
            <div className="h-[250px] w-full" data-id="element-1436" data-mp="1ueuwblra7r7b">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1437" data-mp="1ug60vnsjx2zh">
                <AreaChart data={spendingTrends} margin={{
                top: 10,
                right: 0,
                left: -20,
                bottom: 0
              }} data-id="element-1438" data-mp="2869exrha1qdu">
                  <defs data-id="element-1439" data-mp="1fnlxcm1hothi">
                    <linearGradient id="colorSpending" x1="0" y1="0" x2="0" y2="1" data-id="element-1440" data-mp="1aqvi20xie1x5">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} data-id="element-1441" data-mp="3r4xkwne2yexh" />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} data-id="element-1442" data-mp="3ic3ze881ijb6" />
                    </linearGradient>
                    <linearGradient id="colorBudget" x1="0" y1="0" x2="0" y2="1" data-id="element-1443" data-mp="dx49iuijd0qp">
                      <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3} data-id="element-1444" data-mp="4w62028w2b3d" />
                      <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0} data-id="element-1445" data-mp="2poq0i4ks7dof" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} data-id="element-1446" data-mp="304ik37tmghku" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} data-id="element-1447" data-mp="2r7huixutgdft" />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `€${value}`} data-id="element-1448" data-mp="3ev99yabnl6lb" />
                  <Tooltip contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '0.75rem',
                  color: 'hsl(var(--foreground))'
                }} itemStyle={{
                  color: 'hsl(var(--foreground))'
                }} data-id="element-1449" data-mp="23e9l5ooil9w1" />
                  <Area type="monotone" dataKey="spending" name="Uitgaven" stroke="hsl(var(--primary))" fill="url(#colorSpending)" strokeWidth={2} data-id="element-1450" data-mp="232qn0z87ddps" />
                  <Area type="monotone" dataKey="budget" name="Budget" stroke="hsl(var(--success))" fill="url(#colorBudget)" strokeWidth={2} data-id="element-1451" data-mp="2h29nr6yopms2" />
                  <Legend wrapperStyle={{
                  paddingTop: '10px'
                }} data-id="element-1452" data-mp="mqbkrmqyxn4m" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* Category Pie Chart */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="bg-card border border-border rounded-2xl overflow-hidden" data-id="element-1453" data-mp="epnjxqm60tbo">
          <div className="p-5 border-b border-border" data-id="element-1454" data-mp="xmoppdnjmxgo">
            <div className="flex items-center gap-3" data-id="element-1455" data-mp="xlvikbkoa5y0">
              <div className="p-2.5 rounded-xl bg-primary/10 text-primary" data-id="element-1456" data-mp="1cbb67iowpv9h">
                <Package className="w-5 h-5" data-id="element-1457" data-mp="17c8aiouth1e6" />
              </div>
              <h2 className="text-lg font-semibold text-foreground" data-id="element-1458" data-mp="17e1iq8l2vtoa">
                Waar geef je aan uit?
              </h2>
            </div>
          </div>
          <div className="p-5" data-id="element-1459" data-mp="1haxk7uck7fjc">
            {categoryData.length > 0 ? <div className="flex flex-col items-center gap-6" data-id="element-1460" data-mp="1zyoiyd0qa4rp">
                <div className="h-[200px] w-full" data-id="element-1461" data-mp="1zwc0zzuyzuen">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-1462" data-mp="24xrnzxgpkazo">
                    <PieChart data-id="element-1463" data-mp="1qxqlkdksovi3">
                      <Pie data={categoryData} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" stroke="none" data-id="element-1464" data-mp="1qyjo12cq0yuf">
                        {categoryData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1465" data-mp="1878wqk332k20" data-mpi={index} />)}
                      </Pie>
                      <Tooltip formatter={(value: number) => `€${value.toFixed(2)}`} contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0.75rem'
                  }} data-id="element-1466" data-mp="1c8c8ovi0fvxy" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-full grid grid-cols-2 gap-3" data-id="element-1467" data-mp="19ph9lyvakzgf">
                  {categoryData.map(category => <div key={category.name} className="flex items-center gap-2" data-id="element-1468" data-mp="14qxm3u11vfly">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{
                  backgroundColor: category.color
                }} data-id="element-1469" data-mp="zsw6jj2u1yf8" />
                      <span className="text-sm text-muted-foreground truncate" data-id="element-1470" data-mp="lzwrtjavk88q">
                        {category.name}
                      </span>
                      <span className="text-sm font-medium text-foreground ml-auto" data-id="element-1471" data-mp="gzihtvppqo82">
                        €{category.value.toFixed(0)}
                      </span>
                    </div>)}
                </div>
              </div> : <div className="text-center py-8" data-id="element-1472" data-mp="2snnjpz9chbso">
                <p className="text-muted-foreground" data-id="element-1473" data-mp="2smc9phpb37t4">
                  Nog geen uitgaven data beschikbaar.
                </p>
              </div>}
          </div>
        </motion.div>
      </div>
    </div>;
}